import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Rocket, Sparkles, TrendingUp, Globe, Users, Award } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const HERO_BG_GRADIENT =
  "radial-gradient(ellipse at 50% 30%, rgba(70,70,70,0.35) 0%, rgba(10,10,10,0.95) 55%, #000 100%)";

// Personal Issy photos
const IMG_MEETING = "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/jfgqb72o_IMG_0601%202.webp";
const IMG_BAR = "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/iq5procw_IMG_0600%202.webp";
const IMG_SUPERCARS = "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/r6caid1m_IMG_0599.webp";
const IMG_GROUP_MEAL = "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/wp9dm0yl_IMG_0598%202.webp";
const IMG_WALLET_10K = "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/2i3zqllg_IMG_0CA5882BA7C6-1.jpeg_202607220947.jpg";

const TIMELINE = [
  {
    year: "2019",
    age: "Age 18",
    icon: Rocket,
    title: "First steps in the market",
    desc: "Discovered crypto and started studying charts. No mentors, no roadmap — just raw curiosity and long nights on the screens.",
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/bkp4r9m1_98B480EB-4C0F-4878-B69E-26B593854A54.JPG",
  },
  {
    year: "2021",
    age: "The SafeMoon flip",
    icon: Sparkles,
    title: "$100 → $10,000 in one month",
    desc: "Bought into SafeMoon in 2021 and turned $100 into $10K in 30 days. That's when I knew we were in the matrix — and I needed to escape.",
    img: IMG_WALLET_10K,
    imgContain: true,
  },
  {
    year: "2022 – 2024",
    age: "Networking era",
    icon: Users,
    title: "Building the network",
    desc: "Five years of grinding, connecting with real operators, learning from mistakes, and quietly building the relationships that would become The Syndicate.",
    img: IMG_GROUP_MEAL,
  },
  {
    year: "Aug 2024",
    age: "Founding Partner",
    icon: Award,
    title: "The Syndicate is born",
    desc: "Founded The Syndicate — a home for crypto, stocks and forex traders to come together and generate real wealth. One roof. One family.",
    img: IMG_MEETING,
  },
  {
    year: "2025",
    age: "Six-figure year",
    icon: TrendingUp,
    title: "Six figures on memecoins",
    desc: "Generated six figures trading memecoins in 2025. Proof of concept — the system works, the family eats.",
    img: IMG_SUPERCARS,
  },
  {
    year: "Today",
    age: "Living the mission",
    icon: Globe,
    title: "Travel. Family. Freedom.",
    desc: "Travelling the world, taking care of the people I love, and doubling down on The Syndicate — because the mission has only just started.",
    img: IMG_BAR,
  },
];

// Interactive timeline with scroll-driven progress line + item reveals
function Timeline({ items }) {
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(() => items.map(() => false));

  // Scroll progress for center line fill
  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Progress: 0 when top hits mid-viewport, 1 when bottom hits mid-viewport
      const start = rect.top - vh * 0.5;
      const end = rect.bottom - vh * 0.5;
      const total = end - start;
      const p = total > 0 ? 1 - (end / total) : 0;
      setProgress(Math.max(0, Math.min(1, p)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // IntersectionObserver reveals each item
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute("data-index"));
          if (entry.isIntersecting) {
            setVisible((prev) => {
              if (prev[idx]) return prev;
              const next = [...prev];
              next[idx] = true;
              return next;
            });
          }
        });
      },
      { threshold: 0.25, rootMargin: "0px 0px -10% 0px" }
    );
    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  return (
    <div ref={containerRef} className="relative">
      {/* Center rail (desktop) */}
      <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-white/10" />
      {/* Progress fill overlay */}
      <div
        className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 w-[2px] bg-gradient-to-b from-white via-white to-white/40 shadow-[0_0_18px_rgba(255,255,255,0.6)] transition-[height] duration-150 ease-out"
        style={{ height: `${progress * 100}%` }}
      />
      {/* Traveling comet */}
      <div
        className="hidden md:block absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white shadow-[0_0_24px_8px_rgba(255,255,255,0.35)] transition-[top] duration-150 ease-out"
        style={{ top: `calc(${progress * 100}% - 6px)` }}
      />

      {/* Mobile rail */}
      <div className="md:hidden absolute left-6 top-0 bottom-0 w-px bg-white/10" />
      <div
        className="md:hidden absolute left-6 top-0 w-[2px] bg-gradient-to-b from-white to-white/50 shadow-[0_0_14px_rgba(255,255,255,0.5)] transition-[height] duration-150 ease-out"
        style={{ height: `${progress * 100}%` }}
      />

      <div className="space-y-16 md:space-y-28">
        {items.map((t, i) => {
          const isLeft = i % 2 === 0;
          const isVisible = visible[i];
          return (
            <div
              key={t.year}
              ref={(el) => (itemRefs.current[i] = el)}
              data-index={i}
              data-testid={`timeline-${i}`}
              className="relative md:grid md:grid-cols-2 md:gap-12 items-center pl-16 md:pl-0"
            >
              {/* Content */}
              <div
                className={`${isLeft ? "md:pr-10 md:text-right" : "md:pl-10 md:col-start-2"} order-2 md:order-none transition-all duration-[900ms] ease-out ${
                  isVisible
                    ? "opacity-100 translate-x-0 blur-0"
                    : `opacity-0 blur-[2px] ${isLeft ? "md:-translate-x-8" : "md:translate-x-8"} translate-x-4`
                }`}
              >
                <p className="font-mono-serif text-[10px] tracking-[0.35em] text-white/50">
                  <span className={`inline-block px-2 py-1 rounded-full border transition-colors duration-500 ${
                    isVisible ? "border-white/40 bg-white/5" : "border-white/10"
                  }`}>
                    {t.year} • {t.age}
                  </span>
                </p>
                <h3 className="mt-4 font-display text-3xl md:text-4xl">{t.title}</h3>
                <p className="mt-4 text-white/65 text-base md:text-[17px] leading-relaxed">{t.desc}</p>
              </div>

              {/* Dot (desktop) */}
              <div className={`hidden md:flex absolute left-1/2 -translate-x-1/2 w-14 h-14 rounded-full items-center justify-center z-10 transition-all duration-700 ${
                isVisible
                  ? "bg-black border-2 border-white scale-100 shadow-[0_0_30px_rgba(255,255,255,0.35)]"
                  : "bg-black border border-white/20 scale-90"
              }`}>
                <t.icon size={20} className={`transition-colors duration-500 ${isVisible ? "text-white" : "text-white/50"}`} />
                {isVisible && (
                  <span className="absolute inset-0 rounded-full border border-white/40 animate-ping-slow" />
                )}
              </div>

              {/* Dot (mobile - on left rail) */}
              <div className={`md:hidden absolute left-6 -translate-x-1/2 top-1 w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-700 ${
                isVisible
                  ? "bg-black border-2 border-white shadow-[0_0_20px_rgba(255,255,255,0.35)]"
                  : "bg-black border border-white/20"
              }`}>
                <t.icon size={14} className="text-white" />
              </div>

              {/* Image */}
              <div
                className={`${isLeft ? "md:col-start-2 md:pl-10" : "md:col-start-1 md:row-start-1 md:pr-10"} order-1 md:order-none mb-6 md:mb-0 transition-all duration-[1000ms] ease-out ${
                  isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-[0.97]"
                }`}
              >
                <div className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 hover:border-white/25 transition">
                  <img
                    src={t.img}
                    alt={t.title}
                    className={`w-full h-full ${t.imgContain ? "object-contain p-4 bg-[#0a0a0a]" : "object-cover"} group-hover:scale-105 transition duration-700`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="about-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 md:pt-44 pb-20 px-4 md:px-6">
        <div
          className="absolute inset-0 opacity-95"
          style={{ background: HERO_BG_GRADIENT }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black" />
        <div className="absolute inset-0 dot-pattern opacity-30" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            ABOUT // THE FOUNDING PARTNER
          </p>
          <h1 className="mt-4 font-display text-6xl md:text-8xl tracking-tight">
            Issy
          </h1>
          <p className="mt-3 font-mono-serif text-xs md:text-sm tracking-[0.35em] text-white/60">
            ISSY&nbsp;&nbsp;FLASH
          </p>
          <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Founding Partner of The Syndicate. Six years in the crypto markets. Six figures in memecoins. One mission — bring real traders together and generate real wealth.
          </p>
        </div>
      </section>

      {/* INTRO CARD */}
      <section className="relative z-10 px-4 md:px-6 pb-12">
        <div className="max-w-3xl mx-auto frame p-8 md:p-12">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">IN MY OWN WORDS</p>
          <div className="mt-6 space-y-4 text-white/75 text-base md:text-lg leading-relaxed">
            <p>
              I've been in the crypto market for <span className="text-white font-semibold">6 years</span>. Over that time I've made mistakes — big ones — and I've learned massively from every single one of them.
            </p>
            <p>
              When I first started I was only 18. I bought into SafeMoon in 2021 and turned <span className="text-white font-semibold">$100 into $10,000 in just one month</span>. From that moment I knew we were in the matrix and I needed to escape.
            </p>
            <p>
              I spent the next 5 years networking, growing and refining my edge. In <span className="text-white font-semibold">August 2024</span> I created The Syndicate — a place where traders from every background (crypto, stocks, forex) can come together and generate wealth.
            </p>
            <p>
              In <span className="text-white font-semibold">2025 I generated six figures trading memecoins</span>. Life has been good — I've travelled the world and been able to treat my loved ones. Now the mission is bigger than me. It's the family.
            </p>
          </div>
        </div>
      </section>

      {/* VIDEO section removed — video moved back to Home page */}

      {/* TIMELINE */}
      <section className="relative z-10 px-4 md:px-6 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 md:mb-20">
            <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">THE JOURNEY</p>
            <h2 className="mt-3 font-display text-4xl md:text-6xl tracking-tight">Six years. One story.</h2>
          </div>

          <Timeline items={TIMELINE} />
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="relative z-10 px-4 md:px-6 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <blockquote className="font-display text-3xl md:text-5xl italic text-white/90 leading-tight">
            "The mission is bigger than the money. It always was."
          </blockquote>
          <p className="mt-6 font-mono-serif text-[10px] tracking-[0.3em] text-white/50">
            — ISSY, FOUNDING PARTNER
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 px-4 md:px-6 pb-24">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <h2 className="font-display text-4xl md:text-5xl">Move with the family</h2>
          <p className="mt-4 text-white/60">Entry is earned — not bought.</p>
          <Link
            to="/pricing"
            data-testid="about-join-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            APPLY FOR ENTRY <ArrowRight size={16} />
          </Link>
          <p className="mt-6 italic text-white/50">"Once you're in, you move with the family."</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
